import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { auth } from './firebase';
import {
  IMPERSONATION_HEADER,
  IMPERSONATION_INVALID_CODE,
  clearImpersonationSessionId,
  getImpersonationSessionId,
} from './impersonation';

const TOKEN_STORAGE_KEY = 'firebase_id_token';
const USER_STORAGE_KEY = 'clareef_user';
const DEFAULT_LOGIN_PATH = '/login';
const MAX_REAUTH_ATTEMPTS = 3;

// Set by VendorAuthorizationFilter (backend) once a trial's end date has passed.
// Refreshing the token can't fix this — it's a business-status block, not an
// auth problem — so we skip the reauth-retry loop and sign out immediately.
export const TRIAL_EXPIRED_CODE = 'TRIAL_EXPIRED';
// sessionStorage key the login page reads on mount to show *why* it bounced
// them back, since window.location.href is a hard navigation that discards
// React state — the message has to survive outside of it.
export const TRIAL_EXPIRED_MESSAGE_KEY = 'trial_expired_message';

/**
 * Options for creating the Firebase-backed RTK Query base query with reauth.
 */
export interface CreateFirebaseBaseQueryWithReauthOptions {
  /** Base URL for the API (e.g. import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api') */
  baseUrl: string;
  /** Path to redirect to on auth failure after retries. Defaults to '/login'. */
  loginPath?: string;
}

/**
 * Creates an RTK Query base query that:
 * - Uses Firebase ID token in Authorization header (refreshes from auth.currentUser when available).
 * - Adds X-Impersonation-Session header when an impersonation session id is present in localStorage.
 * - On 401, attempts to refresh the token and retry the request up to MAX_REAUTH_ATTEMPTS.
 * - On 401 with IMPERSONATION_SESSION_INVALID, clears the impersonation session and signs out.
 * - Redirects to loginPath only when refresh fails or retries are exhausted.
 *
 * Use this as the baseQuery for createApi() in promoter, vendor, and prospect apps.
 */
export function createFirebaseBaseQueryWithReauth(
  options: CreateFirebaseBaseQueryWithReauthOptions
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  const { baseUrl, loginPath = DEFAULT_LOGIN_PATH } = options;

  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers) => {
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const token = await currentUser.getIdToken();
          headers.set('Authorization', `Bearer ${token}`);
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(TOKEN_STORAGE_KEY, token);
          }
        } else {
          const storedToken =
            typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
          if (storedToken) {
            headers.set('Authorization', `Bearer ${storedToken}`);
          }
        }
      } catch (error) {
        console.error('Error getting Firebase token:', error);
        const storedToken =
          typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
        if (storedToken) {
          headers.set('Authorization', `Bearer ${storedToken}`);
        }
      }

      const impersonationSessionId = getImpersonationSessionId();
      if (impersonationSessionId) {
        headers.set(IMPERSONATION_HEADER, impersonationSessionId);
      }

      return headers;
    },
  });

  const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
  > = async (args, api, extraOptions) => {
    let lastResult: Awaited<ReturnType<typeof baseQuery>> = await baseQuery(args, api, extraOptions);

    if (isImpersonationInvalid(lastResult.error)) {
      await handleImpersonationInvalid(loginPath);
      return lastResult;
    }

    if (isTrialExpired(lastResult.error)) {
      await handleTrialExpired(lastResult.error, loginPath);
      return lastResult;
    }

    for (let attempt = 0; attempt < MAX_REAUTH_ATTEMPTS; attempt++) {
      if (!lastResult.error || lastResult.error.status !== 401) {
        return lastResult;
      }

      try {
        const newToken = await auth.currentUser?.getIdToken(true);
        if (!newToken) {
          break;
        }
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
        }
        lastResult = await baseQuery(args, api, extraOptions);

        if (isImpersonationInvalid(lastResult.error)) {
          await handleImpersonationInvalid(loginPath);
          return lastResult;
        }
      } catch {
        break;
      }
    }

    if (lastResult.error && lastResult.error.status === 401) {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(USER_STORAGE_KEY);
      }
      clearImpersonationSessionId();
      try {
        await auth.signOut();
      } catch (error) {
        console.error('Error signing out after 401:', error);
      }
      window.location.href = loginPath;
    }

    return lastResult;
  };

  return baseQueryWithReauth;
}

function isImpersonationInvalid(error: FetchBaseQueryError | undefined): boolean {
  if (!error || error.status !== 401) return false;
  const data = (error.data ?? {}) as { code?: string };
  return data.code === IMPERSONATION_INVALID_CODE;
}

function isTrialExpired(error: FetchBaseQueryError | undefined): boolean {
  if (!error || error.status !== 401) return false;
  const data = (error.data ?? {}) as { code?: string };
  return data.code === TRIAL_EXPIRED_CODE;
}

async function handleTrialExpired(error: FetchBaseQueryError | undefined, loginPath: string): Promise<void> {
  const data = (error?.data ?? {}) as { error?: string };
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(
      TRIAL_EXPIRED_MESSAGE_KEY,
      data.error || 'Your trial has expired. Please contact welcome@clareefai.com to reactivate your account.'
    );
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  }
  try {
    await auth.signOut();
  } catch (error) {
    console.error('Error signing out after trial expiry:', error);
  }
  window.location.href = loginPath;
}

async function handleImpersonationInvalid(loginPath: string): Promise<void> {
  clearImpersonationSessionId();
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  }
  try {
    await auth.signOut();
  } catch (error) {
    console.error('Error signing out after invalid impersonation session:', error);
  }
  window.location.href = loginPath;
}
